import { WORLD_CHUNK_SIZE, WORLD_HALF_SIZE } from "@endclock/shared";

export const TERRAIN_GPU_RADIUS = 2;
export const CHUNK_PREFETCH_RADIUS = 3;
export const CHUNK_EVICTION_RADIUS = 3;

export interface WorldChunkCoordinate {
  readonly x: number;
  readonly z: number;
  readonly id: string;
  readonly distance: number;
}

export function worldChunkAt(x: number, z: number): WorldChunkCoordinate {
  const chunkX = Math.floor(x / WORLD_CHUNK_SIZE);
  const chunkZ = Math.floor(z / WORLD_CHUNK_SIZE);
  return { x: chunkX, z: chunkZ, id: worldChunkId(chunkX, chunkZ), distance: 0 };
}

export function worldChunkId(chunkX: number, chunkZ: number): string {
  return `${chunkX}:${chunkZ}`;
}

export function chunkDistance(
  chunkX: number,
  chunkZ: number,
  centerX: number,
  centerZ: number,
): number {
  return Math.max(Math.abs(chunkX - centerX), Math.abs(chunkZ - centerZ));
}

/** Closest first; movement direction only breaks equal-distance ties. */
export function prioritizedChunkWindow(
  centerX: number,
  centerZ: number,
  radius: number,
  velocityX = 0,
  velocityZ = 0,
): readonly WorldChunkCoordinate[] {
  const result: WorldChunkCoordinate[] = [];
  const velocityLength = Math.hypot(velocityX, velocityZ);
  const directionX = velocityLength > 0.05 ? velocityX / velocityLength : 0;
  const directionZ = velocityLength > 0.05 ? velocityZ / velocityLength : 0;
  const renderingMargin = WORLD_CHUNK_SIZE * CHUNK_EVICTION_RADIUS;
  for (let offsetZ = -radius; offsetZ <= radius; offsetZ += 1) {
    for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
      const chunkX = centerX + offsetX;
      const chunkZ = centerZ + offsetZ;
      const originX = chunkX * WORLD_CHUNK_SIZE;
      const originZ = chunkZ * WORLD_CHUNK_SIZE;
      if (
        originX + WORLD_CHUNK_SIZE < -WORLD_HALF_SIZE - renderingMargin ||
        originX > WORLD_HALF_SIZE + renderingMargin ||
        originZ + WORLD_CHUNK_SIZE < -WORLD_HALF_SIZE - renderingMargin ||
        originZ > WORLD_HALF_SIZE + renderingMargin
      ) {
        continue;
      }
      result.push({
        x: chunkX,
        z: chunkZ,
        id: worldChunkId(chunkX, chunkZ),
        distance: Math.max(Math.abs(offsetX), Math.abs(offsetZ)),
      });
    }
  }
  result.sort((left, right) => {
    if (left.distance !== right.distance) return left.distance - right.distance;
    const leftAhead = (left.x - centerX) * directionX + (left.z - centerZ) * directionZ;
    const rightAhead = (right.x - centerX) * directionX + (right.z - centerZ) * directionZ;
    if (leftAhead !== rightAhead) return rightAhead - leftAhead;
    return left.z - right.z || left.x - right.x;
  });
  return result;
}
